import { FIRST_CLASS_LABEL, type AttendanceMark, type SessionAttendee, type SessionWaitingVisit } from "@teamna/shared";
import { Avatar } from "./Avatar.tsx";
import { Badge, type BadgeTone } from "./Badge.tsx";
import { Button } from "./Button.tsx";
import { ChatIcon } from "./Icons.tsx";
import { HealthNoteBadge } from "./HealthNoteBadge.tsx";

/**
 * Who is on a session, for the owner about to run it.
 *
 * The register, not a summary. "6 of 8 booked" tells an owner the room is nearly full; it does not
 * tell them Sara is not coming, and the second is what they are looking at their phone on the pool
 * deck for. So: every name, the member behind it when that is a different person, and a phone
 * number that dials.
 *
 * The people who dropped out stay on the list, greyed and moved to the bottom. An owner opening a
 * session an hour before it runs mostly wants to know what changed, and a list that silently
 * removed the cancellations could not answer that at all.
 *
 * `onMessage` is the second way to reach the member, next to the call. The list has no idea which
 * business it belongs to, so the screen that does passes the handler and the row only shows the
 * affordance when one is there.
 *
 * `onMark` turns the register into "mark who came": each confirmed row gets Present and Absent,
 * a marked row shows its mark with Undo, and "Mark all present" marks every row not marked yet.
 * The screen passes it only from the session's day on (`attendanceOpen`). A mark is a record and
 * never moves a balance.
 *
 * `waiting`: the names that wait for a parent and came (no booking yet). They show after the
 * bookings as Present and Not booked, so the list holds everyone who came (#ai-feedback 2026-10-26).
 */
export function AttendeeList({
  attendees,
  waiting = [],
  onMessage,
  onMark,
  marking = false,
}: {
  attendees: SessionAttendee[];
  waiting?: readonly SessionWaitingVisit[];
  /** Opens the business's thread with the member behind a row. Hidden when absent. */
  onMessage?: (memberUserId: string) => void;
  /** Marks who came. One or more bookings at a time; null clears a mark. Hidden when absent. */
  onMark?: (marks: Array<{ bookingId: string; attendance: AttendanceMark | null }>) => void;
  /** A mark is on its way, so the controls wait. */
  marking?: boolean;
}) {
  const confirmed = attendees.filter((row) => row.status === "confirmed");
  const gone = attendees.filter((row) => row.status !== "confirmed");
  const unmarked = confirmed.filter((row) => !row.attendance);

  return (
    <div className="flex flex-col gap-3">
      {onMark && confirmed.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[13px] text-ink-2">
            {unmarked.length === 0
              ? "Everyone is marked."
              : `${unmarked.length} of ${confirmed.length} not marked yet.`}
          </p>
          {unmarked.length > 0 ? (
            <Button
              variant="secondary"
              disabled={marking}
              onClick={() => onMark(unmarked.map((row) => ({ bookingId: row.bookingId, attendance: "present" })))}
            >
              Mark all present
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className="overflow-hidden rounded-card border border-line">
        {confirmed.length === 0 && waiting.length === 0 ? (
          <p className="px-4 py-4 text-[14px] text-ink-2">
            Nobody is booked on this session yet.
          </p>
        ) : (
          <>
            {confirmed.map((row, index) => (
              <AttendeeRow
                key={row.bookingId}
                attendee={row}
                last={index === confirmed.length - 1 && waiting.length === 0}
                onMessage={onMessage}
                onMark={onMark}
                marking={marking}
              />
            ))}
            {waiting.map((visit, index) => (
              <WaitingRow key={visit.id} visit={visit} last={index === waiting.length - 1} />
            ))}
          </>
        )}
      </div>

      {gone.length > 0 ? (
        <div>
          <p className="eyebrow pb-1.5 text-ink-2">
            {gone.length === 1 ? "1 cancellation" : `${gone.length} cancellations`}
          </p>
          <div className="overflow-hidden rounded-card border border-line">
            {gone.map((row, index) => (
              <AttendeeRow
                key={row.bookingId}
                attendee={row}
                last={index === gone.length - 1}
                onMessage={onMessage}
                muted
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AttendeeRow({
  attendee,
  last,
  muted = false,
  onMessage,
  onMark,
  marking = false,
}: {
  attendee: SessionAttendee;
  last: boolean;
  muted?: boolean;
  onMessage?: (memberUserId: string) => void;
  onMark?: (marks: Array<{ bookingId: string; attendance: AttendanceMark | null }>) => void;
  marking?: boolean;
}) {
  const status = attendee.attendance ? markBadge(attendee.attendance) : attendeeBadge(attendee);
  const who = attendee.memberName ?? attendee.participantName;

  return (
    <div
      className={[
        "flex items-start gap-3 px-4 py-3",
        last ? "" : "border-b border-line",
        muted ? "bg-sunken/40" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Avatar
        name={attendee.participantName}
        photoUrl={attendee.photoUrl ?? null}
        size={36}
        className={muted ? "opacity-60" : ""}
      />
      <div className="min-w-0 flex-1">
        <p
          dir="auto"
          className={[
            "truncate text-left text-[15px] font-semibold",
            muted ? "text-ink-2 line-through decoration-line-strong" : "text-ink",
          ].join(" ")}
        >
          {attendee.participantName}
        </p>
        {attendee.healthNote && !muted ? (
          <span className="mt-0.5 inline-block">
            <HealthNoteBadge />
          </span>
        ) : null}
        {/*
          The member's own name only when it is somebody else. On an adult booking their own place
          the API sends null rather than repeating the name they are already reading.
        */}
        {attendee.memberName ? (
          <p dir="auto" className="mt-0.5 truncate text-left text-[13px] text-ink-2">
            {attendee.memberName}
          </p>
        ) : null}
        {/*
          A real tel: link. The reason an owner opens this list at all is usually to chase somebody,
          and a number you have to retype into the dialler is a number that does not get called.
        */}
        <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          {attendee.memberPhone ? (
            <a
              href={`tel:${attendee.memberPhone}`}
              className="inline-block text-[13px] font-semibold text-accent-strong tabular-nums"
            >
              {attendee.memberPhone}
            </a>
          ) : null}
          {onMessage ? (
            <button
              type="button"
              onClick={() => onMessage(attendee.memberUserId)}
              aria-label={`Message ${who}`}
              className="inline-flex min-h-8 items-center gap-1 text-[13px] font-semibold text-accent-strong"
            >
              <ChatIcon size={15} />
              Message
            </button>
          ) : null}
        </span>
        {attendee.cancelNote ? (
          <p dir="auto" className="mt-1 text-[12px] text-ink-2">{attendee.cancelNote}</p>
        ) : null}
        {onMark ? (
          <fieldset aria-label={`Did ${attendee.participantName} come?`} className="mt-2 flex flex-wrap gap-2 border-0 p-0">
            {attendee.attendance ? (
              <Button
                variant="ghost"
                disabled={marking}
                aria-label={`Undo the mark for ${attendee.participantName}`}
                onClick={() => onMark([{ bookingId: attendee.bookingId, attendance: null }])}
              >
                Undo
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  disabled={marking}
                  aria-label={`${attendee.participantName} is present`}
                  onClick={() => onMark([{ bookingId: attendee.bookingId, attendance: "present" }])}
                >
                  Present
                </Button>
                <Button
                  variant="secondary"
                  disabled={marking}
                  aria-label={`${attendee.participantName} is absent`}
                  onClick={() => onMark([{ bookingId: attendee.bookingId, attendance: "absent" }])}
                >
                  Absent
                </Button>
              </>
            )}
          </fieldset>
        ) : null}
      </div>
      <span className="flex shrink-0 flex-col items-end gap-1 pt-0.5">
        <Badge tone={status.tone}>{status.label}</Badge>
        {/* A marked row reads as its mark, so it says booked or not booked under it. */}
        {attendee.attendance && attendee.status === "confirmed" ? <Badge tone="quiet">{bookedLabel(attendee)}</Badge> : null}
        {/* C3: the person's first class at the business, so the team can greet them. */}
        {attendee.firstClass && !muted ? <Badge tone="info">{FIRST_CLASS_LABEL}</Badge> : null}
        {/* Added anyway by the owner (#ai-feedback 2026-10-26): owes one session until a package pays it, or free. */}
        {attendee.status === "confirmed" && attendee.ownerOverride ? (
          <Badge tone={attendee.ownerOverride === "owed" ? "late" : "quiet"}>{attendee.ownerOverride === "owed" ? "Owes 1 session" : "Free"}</Badge>
        ) : null}
      </span>
    </div>
  );
}

/** A name that waits for a parent and came: no booking, no member, no phone yet. */
function WaitingRow({ visit, last }: { visit: SessionWaitingVisit; last: boolean }) {
  return (
    <div className={["flex items-start gap-3 px-4 py-3", last ? "" : "border-b border-line"].filter(Boolean).join(" ")}>
      <Avatar name={visit.name} photoUrl={null} size={36} />
      <div className="min-w-0 flex-1">
        <p dir="auto" className="truncate text-left text-[15px] font-semibold text-ink">
          {visit.name}
        </p>
        <p className="mt-0.5 text-left text-[13px] text-ink-2">Waits for a parent</p>
      </div>
      <span className="flex shrink-0 flex-col items-end gap-1 pt-0.5">
        <Badge tone={markBadge("present").tone}>{markBadge("present").label}</Badge>
        <Badge tone="quiet">Not booked</Badge>
      </span>
    </div>
  );
}

/** How a confirmed booking came: booked ahead, a weekly slot, or added at the session with no booking. */
function bookedLabel(attendee: SessionAttendee): string {
  if (attendee.source === "walk_in") return "Not booked";
  return attendee.source === "enrollment" ? "Weekly slot" : "Booked";
}

/**
 * How one row reads at a glance.
 *
 * A weekly slot is called out rather than folded into "Booked", because the two are different
 * facts to an owner: an open booking is somebody who chose this session, and a weekly slot is
 * somebody the business placed here and who will be here again next week.
 */
export function attendeeBadge(attendee: SessionAttendee): { label: string; tone: BadgeTone } {
  switch (attendee.status) {
    case "confirmed":
      // Added at the session for someone who did not book: a fact, not a booking (#ai-feedback 2026-10-26).
      if (attendee.source === "walk_in") return { label: "Not booked", tone: "quiet" };
      return attendee.source === "enrollment"
        ? { label: "Weekly slot", tone: "quiet" }
        : { label: "Booked", tone: "confirmed" };
    case "cancelled_late":
      return { label: "Late cancel", tone: "late" };
    case "cancelled_by_business":
      return { label: "You cancelled", tone: "cancelled" };
    case "rescheduled":
      return { label: "Moved", tone: "quiet" };
    default:
      return { label: "Cancelled", tone: "cancelled" };
  }
}

/** A marked row reads as its mark. Absent uses the late tone: it is a fact to note, not an error. */
export function markBadge(mark: AttendanceMark): { label: string; tone: BadgeTone } {
  return mark === "present" ? { label: "Present", tone: "confirmed" } : { label: "Absent", tone: "late" };
}
